import { createContext, useContext, useEffect, useState } from "react";
import api from "../helpers/api";

interface UserType {
  id: number;
  email: string;
  hasActiveSubscription: boolean;
  phoneNumber?: string;
  phoneVerified?: boolean;
}

interface AuthContextType {
  user: UserType | null;
  loading: boolean;
  refreshAuth: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refreshAuth();
  }, []);

  const refreshAuth = async () => {
    try {
      setLoading(true);
      const response = await api.get("/user");
      setUser({
        id: response.data.user.id,
        email: response.data.user.email,
        hasActiveSubscription: response.data.user.hasActiveSubscription || false,
        phoneNumber: response.data.user.phoneNumber || "",
        phoneVerified: response.data.user.phoneVerified || false,
      });
    } catch (error) {
      console.log("Error fetching user")
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/logout");
      setUser(null);
    } catch (error) {
      console.error("Logout failed");
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, refreshAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
